/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { useEffect } from "react";

import { useUnityDispatch } from "../../viewModel";
import {
  isDevToolsBuild,
  openAppVersionOverride
} from "../../viewModel/appVersionOverride";
import { createSecretCodeInterceptor } from "../../viewModel/navigation";
import type { NavigationEngine } from "../../viewModel/navigation";

/** The digit sequence that opens the web-app version override overlay. */
const OVERRIDE_CODE: string = "54321";

/** Name used to register/remove the interceptor on the engine. */
const INTERCEPTOR_NAME: string = "appVersionOverrideCode";

/**
 * Registers the hidden `54321` trigger for the developer/test web-app version
 * override, mirroring {@link useSecretCodeReset}.
 *
 * Debug builds only: `isDevToolsBuild()` is a constant `false` in release, so
 * the interceptor is never installed, `54321` is never watched for, and the
 * overlay it would open stays unreachable.
 *
 * The number keys are safe to claim while the overlay itself is open:
 * `NavigationEngine` stops routing non-structural actions once focus sits in a
 * text field, so digits typed into the version input never reach any
 * interceptor.
 *
 * @param engine - The NavigationEngine instance from context.
 */
export function useAppVersionOverrideCode(engine: NavigationEngine): void {
  const dispatch = useUnityDispatch();

  useEffect((): (() => void) | undefined => {
    if (!isDevToolsBuild()) {
      return undefined;
    }

    const interceptor = createSecretCodeInterceptor(OVERRIDE_CODE, (): void => {
      dispatch(openAppVersionOverride());
    });

    engine.getInterceptors().use(INTERCEPTOR_NAME, interceptor);

    return (): void => {
      engine.getInterceptors().remove(INTERCEPTOR_NAME);
    };
  }, [engine, dispatch]);
}
